import type { ApiErrorBody } from '@shared/types';

/**
 * 接口客户端。
 *
 * 后台与 API 同源（admin.havorix.cn），因此不需要 CORS，也不需要绝对地址；
 * 会话是同源 HttpOnly Cookie，前端不碰任何令牌。
 */

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details?: Record<string, string>;

  constructor(status: number, body: ApiErrorBody) {
    super(body.message);
    this.name = 'ApiError';
    this.status = status;
    this.code = body.code;
    this.details = body.details;
  }

  get fieldErrors(): Record<string, string> {
    return this.details ?? {};
  }
}

let unauthorizedHandler: (() => void) | null = null;

export function setUnauthorizedHandler(handler: (() => void) | null): void {
  unauthorizedHandler = handler;
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const init: RequestInit = { method, credentials: 'include', headers: {} };
  if (body !== undefined) {
    (init.headers as Record<string, string>)['content-type'] = 'application/json';
    init.body = JSON.stringify(body);
  }

  const response = await fetch(path, init);
  const text = await response.text();

  let parsed: unknown;
  try {
    parsed = text === '' ? null : JSON.parse(text);
  } catch {
    throw new ApiError(response.status, {
      code: 'internal_error',
      message: `服务返回了无法解析的内容（HTTP ${response.status}）`,
    });
  }

  const envelope = parsed as { ok?: boolean; data?: T; error?: ApiErrorBody } | null;
  if (!response.ok || envelope?.ok !== true) {
    const error: ApiErrorBody =
      envelope?.error ?? { code: 'internal_error', message: `请求失败（HTTP ${response.status}）` };
    if (response.status === 401) unauthorizedHandler?.();
    throw new ApiError(response.status, error);
  }
  return envelope.data as T;
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body ?? {}),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, body ?? {}),
  delete: <T>(path: string) => request<T>('DELETE', path),
};

export function describeError(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;
  return '发生了未知错误，请稍后重试';
}
